import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subject, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs';
import { TranslocoModule } from '@jsverse/transloco';
import { MaterialModule } from '@ukam/ui-kit/material.module';
import { SearchResult } from '@ukam/core/searchs/search-result';
import { ResultService } from '@ukam/core/services/result.service';
import { FilterField } from '@ukam/core/filters/models/filter-field';
import { FilterFieldItem } from '@ukam/core/filters/models/filter-field-item';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { API_BASE_URL } from '@ukam/core/tokens/api-base-url.token';

@Component({
  selector: 'app-list-filter-dialog',
  templateUrl: './list-filter-dialog.component.html',
  imports: [
    CommonModule,
    FormsModule,
    MaterialModule,
    NgScrollbarModule,
    TranslocoModule],
})
export class AppListFilterDialogComponent {
  private readonly apiBaseUrl = inject(API_BASE_URL);

  readonly dialogRef = inject(MatDialogRef<AppListFilterDialogComponent>);
  readonly data = inject<FilterField>(MAT_DIALOG_DATA);

  private http = inject(HttpClient);
  private resultService = inject(ResultService);

  searchTerm = '';
  results: FilterFieldItem[] = [];
  count = 0;
  loading = signal(true);

  private workingItems = new Map<string, FilterFieldItem>();
  private existingKeys = new Set<string>();
  private allItems: FilterFieldItem[] = [];
  private subject = new Subject<string>();
  private subscription = this.subject.pipe(
    debounceTime(400),
    distinctUntilChanged(),
    tap(() => this.loading.set(true)),
    switchMap(searchText => this.fetch(searchText)),
  ).subscribe({
    next: result => this.applyResult(result),
    error: error => {
      this.resultService.showError(error);
      this.loading.set(false);
    },
  });

  constructor() {
    for (const item of this.data.items ?? []) {
      const key = this.keyOf(item);
      this.workingItems.set(key, item);
      this.existingKeys.add(key);
    }

    this.fetch('').subscribe({
      next: result => this.applyResult(result),
      error: error => {
        this.resultService.showError(error);
        this.loading.set(false);
      },
    });
  }

  get allVisibleSelected(): boolean {
    return this.results.length > 0 && this.results.every(i => i.selected);
  }

  search(): void {
    if (this.data.sourceType === 'http_get_list') {
      this.filterLocal(this.searchTerm);
      return;
    }
    this.subject.next(this.searchTerm);
  }

  private filterLocal(searchText: string): void {
    const term = searchText.trim().toLowerCase();
    this.results = !term
      ? this.allItems
      : this.allItems.filter(i => i.name?.toLowerCase().includes(term));
    this.count = this.allItems.length;
  }

  toggleAllVisible(): void {
    const next = !this.allVisibleSelected;
    for (const item of this.results) {
      item.selected = next;
      this.workingItems.set(this.keyOf(item), item);
    }
  }

  toggle(item: FilterFieldItem): void {
    item.selected = !item.selected;
    this.workingItems.set(this.keyOf(item), item);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    const items = Array.from(this.workingItems.values())
      .filter(item => this.existingKeys.has(this.keyOf(item)) || item.selected);
    this.dialogRef.close(items);
  }

  private fetch(searchText: string): Observable<SearchResult<any> | FilterFieldItem[]> {

    if (this.data.sourceType === 'http_get_list') {
      const url = `${this.apiBaseUrl}${this.data.source}`;
      return this.http.get<FilterFieldItem[]>(url);
    }
    else if (this.data.sourceType === 'http-get-search') {
      const url = `${this.apiBaseUrl}${this.data.source}?searchText=${encodeURIComponent(searchText)}&unpaged=false&offset=0&limit=100${this.excludeExistingCodesQuery()}`;
      return this.http.get<SearchResult<any>>(url);
    }

    const body = {
      searchText,
      unpaged: false,
      offset: 0,
      limit: 100,
      ...this.excludeExistingCodesBody(),
    };
    const url = `${this.apiBaseUrl}${this.data.source}`;
    return this.http.post<SearchResult<any>>(url, body);

  }

  private excludeExistingCodesBody(): { code?: { nin: number[] } } {
    const codes = (this.data.items ?? [])
      .map(item => item.code)
      .filter((code): code is number => code != null);

    return codes.length ? { code: { nin: codes } } : {};
  }

  private excludeExistingCodesQuery(): string {
    const codes = (this.data.items ?? [])
      .map(item => item.code)
      .filter((code): code is number => code != null);
    if (!codes.length) return '';

    return codes.map((code, i) => `&code.nin[${i}]=${encodeURIComponent(code)}`).join('');
  }

  private applyResult(result: SearchResult<any> | FilterFieldItem[]): void {

    const merged: FilterFieldItem[] = [];

    for (const raw of Array.isArray(result) ? result : result?.items ?? []) {
      const key = this.keyOf(raw);
      if (this.existingKeys.has(key)) continue;

      const existing = this.workingItems.get(key);
      const item: FilterFieldItem = {
        id: raw.id,
        code: raw.code,
        key: raw.key,
        name: raw.name,
        note: raw.note,
        color: raw.color,
        icon: raw.icon,
        avatar: raw.avatar,
        selected: existing?.selected ?? false,
      };
      this.workingItems.set(key, item);
      merged.push(item);
    }

    if (this.data.sourceType === 'http_get_list') {
      this.allItems = merged;
    }

    this.results = merged;
    this.count = Array.isArray(result) ? merged.length : (result?.count ?? merged.length);
    this.loading.set(false);
  }

  private keyOf(item: any | FilterFieldItem): string {
    return String(item.id ?? item.code ?? item.name ?? '');
  }
}
